// The pictures of the squad chip and the marks key (R1571; mockup round
// 075, cards 16 and 17).
//
// Every picture is the plan of `render/squad-key.ts` painted on a plain 2D
// canvas through `Canvas2DMarks`, the shim the atlas bakes with, and then
// turned into an image URL one time per look, so the chrome's markup stays
// a stable string when the top bar or the key is written again. The key
// draws a hull at map scale and a hold at 0.78 of it, the way card 17
// does, and the chip draws its icon at the 22 px of card 16. A document
// with no 2D canvas — a test page — gets '' and draws no picture.

import { FLEET_BLOOM_HALF_LEN, FLEET_BLOOM_HALF_W } from '../gen/presentation';
import { badgeFont } from '../fast/gfx/atlas';
import { rgba } from '../render/color';
import type { HoldEra, HoldSymbolId } from '../render/hold-marks';
import { shipStyleOf } from '../render/hull-styles';
import { BoxProbe, Canvas2DMarks, type MarkBox } from '../render/marks';
import type { SceneLook } from '../render/scene-look';
import {
  KEY_CLASS, drawKeyCells, drawKeyHold, drawKeyHull, keyHullBox, keyHullPlan,
  type KeyHullPlan, type KeyRank,
} from '../render/squad-key';
import type { KeyPictures, SquadLook } from './squad';
import type { SquadFleetStyle } from './settings';
import { MAX_DPR, deviceRatio } from '../render/dpr';

/** CSS px per map px: a key hull, a key hold and the chip icon. */
const HULL_PX = 1;
const HOLD_PX = 0.78;
/** Card 16 draws 34 map px of the squad mark in a 22 px icon. */
const ICON_PX = 22 / 34;
/** The layers and the alpha of the own hull's bloom (`atlas.ts` 'bloom'). */
const BLOOM_LAYERS = 6;
const BLOOM_ALPHA = 0.07;
/** The numeral the key's `slot-numeral` hull prints: the first slot. */
const KEY_SLOT = '1';

const cache = new Map<string, string>();

/** What one picture paints: its box in map px, and the painter. */
interface Paint {
  box: MarkBox;
  draw(c: CanvasRenderingContext2D): void;
}

/**
 * Paints one picture into a canvas the size of its box, and answers its
 * `<img>` at CSS size, or '' where the document has no 2D canvas. The
 * cache is asked first: the top bar asks for the chip icon four times a
 * second, and only a new look plans and paints.
 */
function picture(key: string, k: number, plan: () => Paint): string {
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  const { box, draw } = plan();
  const pad = 1;
  const w = Math.ceil((box.x1 - box.x0 + pad * 2) * k);
  const h = Math.ceil((box.y1 - box.y0 + pad * 2) * k);
  // The shared cap of every raster (R2249).
  const dpr = Math.max(1, deviceRatio(MAX_DPR));
  const cv = typeof document === 'undefined' ? null : document.createElement('canvas');
  const c = cv?.getContext('2d') ?? null;
  if (!cv || !c) {
    cache.set(key, '');
    return '';
  }
  cv.width = Math.max(1, Math.round(w * dpr));
  cv.height = Math.max(1, Math.round(h * dpr));
  c.scale(dpr * k, dpr * k);
  c.translate(pad - box.x0, pad - box.y0);
  draw(c);
  const out = `<img src="${cv.toDataURL('image/png')}" width="${w}" height="${h}" alt="">`;
  cache.set(key, out);
  return out;
}

/** Paints a hull plan: the bloom, the cells, the hull at its scale, the numeral. */
function paintHull(c: CanvasRenderingContext2D, plan: KeyHullPlan, faction: number): void {
  if (plan.bloom) {
    for (let i = 0; i < BLOOM_LAYERS; i++) {
      const s = 1 - i / BLOOM_LAYERS;
      c.beginPath();
      c.ellipse(0, 0, FLEET_BLOOM_HALF_LEN * s, FLEET_BLOOM_HALF_W * s, 0, 0, Math.PI * 2);
      c.fillStyle = rgba(faction, BLOOM_ALPHA);
      c.fill();
    }
  }
  c.beginPath();
  drawKeyCells(new Canvas2DMarks(c), plan);
  c.save();
  c.scale(plan.scale, plan.scale);
  c.beginPath();
  drawKeyHull(new Canvas2DMarks(c), plan);
  c.restore();
  if (plan.slot) {
    c.font = badgeFont();
    c.textBaseline = 'middle';
    c.textAlign = 'left';
    c.fillStyle = rgba(plan.slot.ink, plan.slot.alpha);
    c.fillText(KEY_SLOT, plan.slot.dx, plan.slot.dy);
  }
}

/** The look fields a hull picture depends on, as a cache key. */
function hullKey(look: SceneLook, rank: KeyRank, ink: number, k: number): string {
  return `hull:${rank}:${look.ownFleet}:${look.squadFleet}:${shipStyleOf(look, KEY_CLASS)}:${ink}:${k}`;
}

/** One hull of the key, or the chip icon, under one look. */
function hullPicture(look: SceneLook, rank: KeyRank, ink: number, k: number): string {
  return picture(hullKey(look, rank, ink, k), k, () => {
    const plan = keyHullPlan(look, rank, ink);
    return { box: keyHullBox(plan), draw: (c) => paintHull(c, plan, ink) };
  });
}

/** One hold of the key under one look. */
function holdPicture(
  look: SceneLook, rank: KeyRank, symbol: HoldSymbolId, era: HoldEra, ink: number,
): string {
  const key = `hold:${rank}:${look.ownHold}:${look.squadHold}:${symbol}:${era}:${ink}`;
  return picture(key, HOLD_PX, () => {
    const probe = new BoxProbe();
    drawKeyHold(probe, look, rank, symbol, era, ink);
    return {
      box: probe.result() ?? { x0: 0, y0: 0, x1: 0, y1: 0 },
      draw: (c) => {
        c.beginPath();
        drawKeyHold(new Canvas2DMarks(c), look, rank, symbol, era, ink);
      },
    };
  });
}

/** The chip icon (card 16): the reader's squad hull, at 22 px. */
export function squadIcon(look: SceneLook, ink: number): string {
  return hullPicture(look, 'mate', ink, ICON_PX);
}

/** The pictures of the marks key (card 17) under the reader's look. */
export function keyPictures(l: SquadLook, ink: number): KeyPictures {
  return {
    hull: (rank: KeyRank, squadFleet?: SquadFleetStyle) =>
      hullPicture(squadFleet ? { ...l.scene, squadFleet } : l.scene, rank, ink, HULL_PX),
    hold: (rank: KeyRank) => holdPicture(l.scene, rank, l.holdSymbol, l.era, ink),
  };
}
